// frontend/src/ChatWindow.js
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import Markdown from 'markdown-to-jsx';
import './ChatWindow.css';
import ChatHistorySidebar from './ChatHistorySidebar';
import ChatSettingsModal from './ChatSettingsModal';
import ExplorerLogModal from './ExplorerLogModal';
import DocumentViewerModal from './DocumentViewerModal';

// --- Configuration ---
const BASE_URL = process.env.REACT_APP_SERVER_URL || '';
const FIVE_MINUTES_TIMEOUT = 300000; // 5 minutes

// --- Helper Functions ---
const haveMessagesChanged = (originalMessages = [], currentMessages = []) => {
    if (!originalMessages && !currentMessages) return false;
    if (!originalMessages || !currentMessages) return true;
    if (originalMessages.length !== currentMessages.length) return true;
    try {
        return JSON.stringify(originalMessages) !== JSON.stringify(currentMessages);
    } catch (e) {
        console.error("Error comparing message arrays:", e);
        return true; // Assume changed if comparison fails
    }
};

// --- Main Component ---
const ChatWindow = forwardRef(({ isAuthenticated, handleLogout, authUsername, userLLMSettings, onSettingsSavedInChatWindow }, ref) => {
    // --- Core State ---
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentChatId, setCurrentChatId] = useState(null); 
    const [selectedChat, setSelectedChat] = useState(null); 
    const [originalLoadedMessages, setOriginalLoadedMessages] = useState([]); 
    const [refreshSidebarKey, setRefreshSidebarKey] = useState(0);
    const [chatHistory, setChatHistory] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null); // <-- ADD THIS LINE

    // --- Refs ---
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const inputBarRef = useRef(null);

    // --- Chat Settings State ---
    const [isChatSettingsOpen, setIsChatSettingsOpen] = useState(false);
    const [useRAG, setUseRAG] = useState(false);
    const [selectedKnowledgeName, setSelectedKnowledgeName] = useState('');
    const [useText2SQL, setUseText2SQL] = useState(false);
    const [selectedDb, setSelectedDb] = useState('');
    const [useAgentAPI, setUseAgentAPI] = useState(false);
    const [seeThoughtsAndActions, setSeeThoughtsAndActions] = useState(false); // Existing state for the flag
    const [loopsBeforeHumanInput, setLoopsBeforeHumanInput] = useState(1);
    const [selectedPipeline, setSelectedPipeline] = useState('');
    const [selectedTools, setSelectedTools] = useState({
        relevantText: true, googlesearch: false, queryDatabases: false, exploreKnowledgeGraph: true,
        searchCollectionKnowledge: true, saveTextToFile: false, listFilesInDirectory: false, readFileContent: false,
        analyticsEngine: false, Knowledge_query: false, listAllMyFiles: false, image_annotation: false, generate_image: false
    });
    const [systemPrompt, setSystemPrompt] = useState(''); // Will hold an object {text, name, source, ...}
    const [attachedFiles, setAttachedFiles] = useState([]);

    // --- Popup State ---
    const [collectionNames, setCollectionNames] = useState([]);
    const [showCollectionPopup, setShowCollectionPopup] = useState(false);
    const [collectionFilter, setCollectionFilter] = useState('');
    const [collectionTriggerIndex, setCollectionTriggerIndex] = useState(-1);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [collectionError, setCollectionError] = useState('');
    const [uniqueRemoteDbs, setUniqueRemoteDbs] = useState([]);
    const [showDbPopup, setShowDbPopup] = useState(false);
    const [dbFilter, setDbFilter] = useState('');
    const [dbTriggerIndex, setDbTriggerIndex] = useState(-1);
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [dbError, setDbError] = useState('');
    const [popupPosition, setPopupPosition] = useState({ bottom: 0, left: 0, width: 0 });

    // --- Explorer Log Modal State ---
    const [isExplorerModalOpen, setIsExplorerModalOpen] = useState(false);
    const [selectedExplorerUUID, setSelectedExplorerUUID] = useState(null);
    const [explorerLogData, setExplorerLogData] = useState(null);
    const [isExplorerModalLoading, setIsExplorerModalLoading] = useState(false);
    const [explorerModalError, setExplorerModalError] = useState('');

     // --- START: Add State for DocumentViewerModal ---
     const [isViewerOpen, setIsViewerOpen] = useState(false);
     const [viewingFilePath, setViewingFilePath] = useState(null); // Path relative to user's Home

     // --- ADDED: Agent Pipeline Pause State ---
     const [pausedAgentStepConversation, setPausedAgentStepConversation] = useState(null);
     const [pausedAgentStepIndex, setPausedAgentStepIndex] = useState(-1);
     const [pausedPipelineName, setPausedPipelineName] = useState('');
     // --- ADDED: Agent Pipeline Pause State ---

     // --- NEW: State and Refs for Virtualization ---
    const [displayedMessageCount, setDisplayedMessageCount] = useState(15); // Show latest 50 messages initially
    const chatAreaRef = useRef(null); // Ref for the scrollable chat area div
    const scrollStateBeforeLoadMore = useRef(null); // Ref to hold scroll position before loading more  

    


    // --- Derived Settings ---
    const getCurrentProviderSettings = useCallback(() => {
        return userLLMSettings
            ? { llmProvider: userLLMSettings.llmprovider || 'OpenAI', model: userLLMSettings.model || 'gpt-4o-mini' }
            : { llmProvider: 'OpenAI', model: 'gpt-4o-mini' };
    }, [userLLMSettings]);
    const [settings, setSettings] = useState(getCurrentProviderSettings());
    useEffect(() => { setSettings(getCurrentProviderSettings()); }, [userLLMSettings, getCurrentProviderSettings]);


    // const displayedMessages = useMemo(() => {
    //     // We use the filtered `processedMessagesForDisplay` array for rendering, and slice it for virtualization.
    //     return processedMessagesForDisplay.slice(-displayedMessageCount);
    // }, [processedMessagesForDisplay, displayedMessageCount]);

    // const loadMoreMessages = useCallback(() => {
    //     const chatArea = chatAreaRef.current;
    //     if (!chatArea) return;
    
    //     // Store the current scroll state *before* we update the state and cause a re-render.
    //     scrollStateBeforeLoadMore.current = {
    //         scrollHeight: chatArea.scrollHeight,
    //         scrollTop: chatArea.scrollTop,
    //     };
    
    //     // Increase the number of displayed messages, capped at the total number of *visible* messages.
    //     setDisplayedMessageCount(prevCount => Math.min(prevCount + 15, processedMessagesForDisplay.length));
    // }, [processedMessagesForDisplay.length]); // Depends on the filtered message list lengt

        // --- START: Process messages for display and virtualization ---
    const processedMessagesForDisplay = useMemo(() => {
        if (seeThoughtsAndActions) {
            return messages; // Show all messages
        }

        // Filter messages: keep system, user, and only the last in a sequence of assistant messages
        const filtered = [];
        for (let i = 0; i < messages.length; i++) {
            const currentMessage = messages[i];
            if (currentMessage.role === 'system' || currentMessage.role === 'user') {
                filtered.push(currentMessage);
            } else if (currentMessage.role === 'assistant') {
                // Check if it's the last message OR the next message is NOT an assistant message
                if (i === messages.length - 1 || messages[i + 1].role !== 'assistant') {
                    filtered.push(currentMessage);
                }
                // Else (it's an assistant message followed by another assistant message), skip it
            } else {
                 // Handle any other unexpected roles by just including them
                 filtered.push(currentMessage);
            }
        }
        return filtered;
    }, [messages, seeThoughtsAndActions]);

    const displayedMessages = useMemo(() => {
        // We use the filtered `processedMessagesForDisplay` array for rendering, and slice it for virtualization.
        return processedMessagesForDisplay.slice(-displayedMessageCount);
    }, [processedMessagesForDisplay, displayedMessageCount]);

    const loadMoreMessages = useCallback(() => {
        const chatArea = chatAreaRef.current;
        if (!chatArea) return;
    
        // Store the current scroll state *before* we update the state and cause a re-render.
        scrollStateBeforeLoadMore.current = {
            scrollHeight: chatArea.scrollHeight,
            scrollTop: chatArea.scrollTop,
        };
    
        // Increase the number of displayed messages, capped at the total number of *visible* messages.
        setDisplayedMessageCount(prevCount => Math.min(prevCount + 15, processedMessagesForDisplay.length));
    }, [processedMessagesForDisplay.length]);
    // --- END: Process messages for display and virtualization ---

    // --- Effects ---
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);   
    
    useLayoutEffect(() => {
        const chatArea = chatAreaRef.current;
        if (!chatArea) return;
    
        // Case 1: Restore scroll position after loading more messages.
        if (scrollStateBeforeLoadMore.current) {
            const { scrollHeight: prevScrollHeight, scrollTop: prevScrollTop } = scrollStateBeforeLoadMore.current;
            const newScrollHeight = chatArea.scrollHeight;
            
            // The magic: new scrollTop is the old scrollTop + the height of the new content added at the top.
            chatArea.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
            
            // Reset the ref so this logic doesn't run again until the next "load more".
            scrollStateBeforeLoadMore.current = null;
        } 
        // Case 2: Auto-scroll to bottom for new messages.
        else {
            // This is a common heuristic: if the user is near the bottom, auto-scroll.
            // A buffer of 150px allows for some leeway and feels natural.
            const isScrolledNearBottom = chatArea.scrollHeight - chatArea.clientHeight <= chatArea.scrollTop + 150;
            
            // Only auto-scroll if the user was already near the bottom.
            // This prevents jumping to the bottom if the user is reading old history and a new message arrives.
            if (isScrolledNearBottom) {
                messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }); // Use 'auto' for an instant, non-animated scroll on new messages.
            }
        }
        // This effect runs whenever the list of displayed messages changes.
    }, [displayedMessages]);

    useEffect(() => {
        if (userLLMSettings && userLLMSettings.chat_settings) {
            const cs = userLLMSettings.chat_settings;
            console.log("ChatWindow: Initializing states from userLLMSettings.chat_settings:", cs);
            setUseRAG(cs.useRAG ?? false);
            setSelectedKnowledgeName(cs.selectedKnowledgeName ?? '');
            setUseText2SQL(cs.useText2SQL ?? false);
            setSelectedDb(cs.selectedDb ?? '');
            setUseAgentAPI(cs.useAgentAPI ?? false);
            setLoopsBeforeHumanInput(cs.loopsBeforeHumanInput ?? 1);
            
            const defaultToolsState = {
                relevantText: true, googlesearch: false, queryDatabases: false, exploreKnowledgeGraph: true,
                searchCollectionKnowledge: true, saveTextToFile: false, listFilesInDirectory: false, readFileContent: false,
                analyticsEngine: false, Knowledge_query: false, listAllMyFiles: false, image_annotation: false, generate_image: false,
            };
            setSelectedTools(cs.selectedTools ? { ...defaultToolsState, ...cs.selectedTools } : defaultToolsState);
            setSelectedPipeline(cs.selectedPipeline ?? '');
            setSeeThoughtsAndActions(cs.seeThoughtsAndActions ?? false); // MODIFIED: Ensure this is updated
            // DO NOT update attachedFiles here, it's session-specific
        } else {
            console.log("ChatWindow: No chat_settings found in userLLMSettings, resetting to defaults.");
            setUseRAG(false);
            setSelectedKnowledgeName('');
            setUseText2SQL(false);
            setSelectedDb('');
            setUseAgentAPI(false);
            setLoopsBeforeHumanInput(1);
            setSelectedTools({
                relevantText: true, googlesearch: false, queryDatabases: false, exploreKnowledgeGraph: true,
                searchCollectionKnowledge: true, saveTextToFile: false, listFilesInDirectory: false, readFileContent: false,
                analyticsEngine: false, Knowledge_query: false, listAllMyFiles: false, image_annotation: false, generate_image: false,
            });
            setSelectedPipeline('');
            setSeeThoughtsAndActions(false);
            // DO NOT reset attachedFiles here unless explicitly intended for all defaults
        }
    }, [userLLMSettings]); 
    
    const handleSaveSettingsAndPersist = useCallback(async (
        newLoops,
        newTools,
        newPipeline,
        dbNameToSave,        
        knowledgeNameToSave, 
        newlyAttachedFiles,  
        newUseRAG,           
        newUseText2SQL,      
        newUseAgentAPI,      
        newSeeThoughts 
    ) => {
        console.log("ChatWindow: handleSaveSettingsAndPersist called with:", { newLoops, newTools, newPipeline, dbNameToSave, knowledgeNameToSave, newlyAttachedFilesCount: newlyAttachedFiles?.length, newUseRAG, newUseText2SQL, newUseAgentAPI, newSeeThoughts });
    
        // Update ChatWindow's local state for these settings
        setLoopsBeforeHumanInput(newLoops);
        setSelectedTools(newTools);
        setSelectedPipeline(newPipeline);
        setSelectedDb(dbNameToSave);
        setSelectedKnowledgeName(knowledgeNameToSave); 
        setAttachedFiles(newlyAttachedFiles);
        setUseRAG(newUseRAG);
        setUseText2SQL(newUseText2SQL);
        setUseAgentAPI(newUseAgentAPI);
        setSeeThoughtsAndActions(newSeeThoughts);
    
        setIsChatSettingsOpen(false); // Close the modal
    
        const chatSettingsPayload = {
            useRAG: newUseRAG,
            selectedKnowledgeName: knowledgeNameToSave,
            useText2SQL: newUseText2SQL,
            selectedDb: dbNameToSave,
            useAgentAPI: newUseAgentAPI,
            loopsBeforeHumanInput: newLoops,
            selectedTools: newTools,
            selectedPipeline: newPipeline,
            seeThoughtsAndActions: newSeeThoughts, 
        };
    
        const globalLLMSettings = userLLMSettings || {
            llmprovider: 'OpenAI', 
            model: 'gpt-4o-mini',
            temperature: 0.5,
            max_tokens: 2500,
        };
        
        const finalPayload = {
            llmProvider: globalLLMSettings.llmprovider, 
            model: globalLLMSettings.model,
            temperature: (globalLLMSettings.temperature ?? 0.5).toString(), // Ensure ?? for null temp
            maxTokens: (globalLLMSettings.max_tokens ?? 2500).toString(), // Ensure ?? for null tokens
            chatSettings: chatSettingsPayload, 
        };
    
        console.log("ChatWindow: Saving user settings with payload:", finalPayload);
        setLoading(true); 
        setError('');
        try {
            await axios.post(`${BASE_URL}/api/user/settings`, finalPayload, { withCredentials: true });
            console.log("ChatWindow: User settings (including chat_settings) saved successfully.");
            // MODIFIED for Problem 3: Removed alert
            // alert("Chat settings applied and saved!"); 
            
            // MODIFIED for Problem 1: Trigger refresh in App.js
            if (onSettingsSavedInChatWindow) {
                onSettingsSavedInChatWindow();
            }
    
        } catch (err) {
            console.error("ChatWindow: Error saving user settings:", err);
            const errorMsg = err.response?.data?.error || err.message || "Failed to save settings.";
            setError(`Save settings failed: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    }, [userLLMSettings, BASE_URL, onSettingsSavedInChatWindow]); // Added onSettingsSavedInChatWindow

    
    const handleCancelSettingsModal = () => {
        console.log("ChatWindow: Chat settings modal closed without saving (Cancel/X/ESC).");
        setIsChatSettingsOpen(false);
        // No state changes related to settings are made here.
        // Modal's internal temporary changes are discarded.
    };

    const fetchHistory = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await axios.get(`${BASE_URL}/api/chat-history`, { withCredentials: true });
            setChatHistory(response.data || []);
        } catch (err) {
            console.error("Failed to fetch chat history:", err);
            setError("Could not load chat history.");
        }
    }, [isAuthenticated, BASE_URL]);

    // useEffect(() => {
    //     fetchHistory();
    // }, [fetchHistory]); 

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory, refreshSidebarKey]);

    const fetchCollectionNames = useCallback(async () => {
        setLoadingCollections(true); setCollectionError('');
        try {
            const response = await axios.get(`${BASE_URL}/api/names`, { withCredentials: true });
            if (Array.isArray(response.data)) { setCollectionNames(response.data); }
            else { setCollectionNames([]); setCollectionError("Invalid data format received."); }
        } catch (error) {
            setCollectionNames([]);
            setCollectionError(error.response?.data?.error || error.message || "Could not load collection names.");
        } finally { setLoadingCollections(false); }
    }, [BASE_URL]);

    const fetchDbNames = useCallback(async () => {
        setLoadingDbs(true); setDbError('');
        try {
            const response = await axios.get(`${BASE_URL}/api/unique_remote_dbs`, { withCredentials: true });
            if (Array.isArray(response.data)) { setUniqueRemoteDbs(response.data); }
            else { setUniqueRemoteDbs([]); setDbError("Invalid data format received."); }
        } catch (error) {
            setUniqueRemoteDbs([]);
            setDbError(error.response?.data?.error || error.message || "Could not load database names.");
        } finally { setLoadingDbs(false); }
    }, [BASE_URL]);

    useEffect(() => {
        fetchCollectionNames();
        fetchDbNames();
    }, [fetchCollectionNames, fetchDbNames]);

    const clearLocalStateAndResetOriginals = useCallback((isNewChatStart = true) => {
        console.log("Clearing local chat state. New chat start:", isNewChatStart);
        setMessages([]);
        setInput('');
        setError('');
        setAttachedFiles([]);
        setLoading(false);
        setDisplayedMessageCount(15);
    
        setPausedAgentStepConversation(null);
        setPausedAgentStepIndex(-1);
        setPausedPipelineName('');
    
        if (isNewChatStart) {
            setCurrentChatId(null);
            setSelectedChat(null);
            setOriginalLoadedMessages([]);
        }
    
        setSelectedDb('');
        setShowCollectionPopup(false); setCollectionFilter(''); setCollectionTriggerIndex(-1);
        setShowDbPopup(false); setDbFilter(''); setDbTriggerIndex(-1);
        setIsExplorerModalOpen(false); setSelectedExplorerUUID(null); setExplorerLogData(null);
        setExplorerModalError(''); setIsExplorerModalLoading(false);
    }, []);

    const saveChatIfNeeded = useCallback(async () => {
        // This is the condition for an unsaved chat.
        if (!selectedChat && messages.length > 0) {
            setLoading(true);
            try {
                console.log("Auto-saving unsaved chat before navigation...");
                // Use the same API endpoint as "Save & New Chat"
                await axios.post(`${BASE_URL}/api/clear`, { messages: messages, promptName: null }, { withCredentials: true });
                console.log("Unsaved chat saved successfully.");
                setRefreshSidebarKey(prev => prev + 1); // Refresh sidebar to show the newly saved chat
                clearLocalStateAndResetOriginals(true); // Clean up the chat window state
                return true; // Indicate success
            } catch (err) {
                console.error("Failed to auto-save chat:", err);
                setLoading(false); // Stop loading indicator on failure
                // Throw an error to be caught by the caller in App.js
                throw new Error("Failed to save chat.");
            }
        }
        // If not "dirty", resolve to true immediately.
        return true;
    }, [selectedChat, messages, clearLocalStateAndResetOriginals, BASE_URL]);


    // Expose the function to the parent component (App.js) via the ref
    useImperativeHandle(ref, () => ({
        handleBeforeNavigate: async () => {
            // Check if there are unsaved changes.
            if (!selectedChat && messages.length > 0) {
                try {
                    await saveChatIfNeeded();
                    return true; // Saved successfully, navigation can proceed.
                } catch (e) {
                    // The save operation failed. Ask the user for confirmation to discard changes.
                    return window.confirm("Failed to save your current chat. Are you sure you want to leave and discard your changes?");
                }
            }
            // No unsaved changes, so navigation can proceed.
            return true;
        }
    }));

    useEffect(() => {
        if (!isAuthenticated) {
            clearLocalStateAndResetOriginals(true);
        }
    }, [isAuthenticated, clearLocalStateAndResetOriginals]);

    // --- Chat History Management ---
    const handleNewChat = useCallback(() => {
        setError('');
        const performSaveAndReset = (isNewChatStart = true) => {
            setLoading(true);
            console.log(`Attempting to save chat (currentChatId: ${currentChatId || 'New'})...`);
            const promptNameToSave = selectedChat?.prompt_name || null;
            axios.post(`${BASE_URL}/api/clear`, { messages: messages, promptName: promptNameToSave }, { withCredentials: true })
                .then(response => {
                    console.log("Chat saved successfully, backend assigned new entry.");
                    clearLocalStateAndResetOriginals(isNewChatStart);
                    setRefreshSidebarKey(prev => prev + 1);
                })
                .catch(err => {
                    console.error("Failed to save chat:", err);
                    const errorMsg = err.response?.data?.error || err.message || "Could not save the chat.";
                    if (err.response?.status === 401) {
                        setError("Authentication failed. Please log in again.");
                        if (handleLogout) handleLogout();
                    } else { setError(`Failed to save chat: ${errorMsg}`); }
                    setLoading(false);
                });
        };

        if (!selectedChat && messages.length > 0) {
            console.log("handleNewChat: Saving current new/forked chat.");
            performSaveAndReset(true);
        } else {
            console.log("handleNewChat: No changes to save, or empty new buffer. Clearing for new chat.");
            clearLocalStateAndResetOriginals(true);
        }
    }, [messages, selectedChat, currentChatId, clearLocalStateAndResetOriginals, BASE_URL, handleLogout]);

    const handleSelectChat = useCallback((chatData) => {
        if (chatData === null) {
            clearLocalStateAndResetOriginals(true);
            return;
        }

        if (!chatData || !chatData.id) {
            console.warn("handleSelectChat called with invalid chatData:", chatData);
            return;
        }
        console.log("Selected chat from sidebar:", chatData.id);

        const isCurrentBufferUnsaved = !selectedChat && messages.length > 0;
        if (isCurrentBufferUnsaved) {
            if (!window.confirm("You have an unsaved chat in progress. Load selected chat and discard current changes?")) {
                return;
            }
        }

        clearLocalStateAndResetOriginals(false);
        setSelectedChat(chatData);
        setCurrentChatId(chatData.id);

        let loadedMessages = [];
        if (chatData.chat_history) {
            try {
                let history = chatData.chat_history;
                if (typeof history === 'string') { try { history = JSON.parse(history); } catch { /* ignore */ } }
                if (typeof history === 'string') { try { history = JSON.parse(history); } catch (e) { console.error("Double JSON parse failed", e); history = null; } }

                if (Array.isArray(history) && history.every(msg => msg && typeof msg.role === 'string' && typeof msg.content !== 'undefined')) {
                    loadedMessages = history.map(msg => ({ ...msg, content: String(msg.content || '') }));
                } else {
                    setError("Could not load chat history: Invalid format.");
                }
            } catch (e) { setError("Failed to parse chat history."); }
        }
        setMessages(loadedMessages);
        setOriginalLoadedMessages([...loadedMessages]);
        setLoading(false);
    }, [messages, selectedChat, clearLocalStateAndResetOriginals]);


    // --- Settings Modal Management ---
    const handleSaveSettings = useCallback((
        newLoops,
        newTools,
        newPipeline,
        dbNameToSave,
        newlyAttachedFiles,
        newUseRAG,
        newUseText2SQL,
        newUseAgentAPI
    ) => {
        setLoopsBeforeHumanInput(newLoops);
        setSelectedTools(newTools);
        setSelectedPipeline(newPipeline);
        setSelectedDb(dbNameToSave);
        setAttachedFiles(newlyAttachedFiles);
        setUseRAG(newUseRAG);
        setUseText2SQL(newUseText2SQL);
        setUseAgentAPI(newUseAgentAPI);
        setIsChatSettingsOpen(false);
    }, []);

    // --- Agent/Pipeline Helpers ---
    const attemptRequest = useCallback(async (currentMessages, provider, tools, chatIdToUse, currentSystemPromptText = '', currentSystemPromptName = null, retry = 0) => {
        console.log(`Calling Agent API (Retry: ${retry}) for Chat ID: ${chatIdToUse || 'New Chat (will be assigned by save)'}`);
        setLoading(true); setError('');
        
        const messagesForAgent = [];
        let systemPromptFoundInMessages = false;
        currentMessages.forEach(msg => {
             messagesForAgent.push(msg);
             if (msg.role === 'system') systemPromptFoundInMessages = true;
        });

        // Add system prompt text to messages array if it's not already there and is provided
        if (currentSystemPromptText && !systemPromptFoundInMessages && !messagesForAgent.some(m => m.role === 'system')) {
            messagesForAgent.unshift({ role: 'system', content: currentSystemPromptText });
        }
        
        try {
            const payload = {
                messages: messagesForAgent,
                human_loop: loopsBeforeHumanInput,
                provider: provider || 'OpenAI',
                selectedTools: tools,
                chat_id: chatIdToUse,
                system_prompt_text: currentSystemPromptText || null, // Actual text for the system message
                system_prompt_name: currentSystemPromptName || null  // Name of the selected prompt
            };
            console.log("Payload to /api/agent-messages:", JSON.stringify(payload, null, 2)); // Log the full payload
            const response = await axios.post(
                `${BASE_URL}/api/agent-messages`,
                payload,
                { timeout: FIVE_MINUTES_TIMEOUT, withCredentials: true }
            );
            return response.data;
        } catch (error) {
             if ((error.code === 'ECONNABORTED' || error.response?.status === 504 || error.response?.status === 502) && retry < 1) {
                 await new Promise(resolve => setTimeout(resolve, 1500));
                 return await attemptRequest(currentMessages, provider, tools, chatIdToUse, currentSystemPromptText, currentSystemPromptName, retry + 1);
             } else {
                 let specificErrorMsg = error.message || 'Failed to receive response after retry.';
                 if (error.response) {
                    specificErrorMsg = error.response.data?.error || specificErrorMsg;
                    if (error.response.status === 401) { setError("Authentication failed."); if (handleLogout) handleLogout(); }
                    else { setError(`Agent Error: ${specificErrorMsg}`); }
                 } else { setError(`Agent Error: ${specificErrorMsg}`); }
                 throw error;
             }
        }
    }, [loopsBeforeHumanInput, BASE_URL, handleLogout]);

    const fetchPipelinePrompts = useCallback(async (pipelineName) => {
        if (!pipelineName) return [];
        try {
            const response = await axios.get(`${BASE_URL}/api/prompt-pipelines/details-by-name/${pipelineName}`, { withCredentials: true });
            if (!Array.isArray(response.data) || !response.data.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) {
                throw new Error('Invalid data format: expected array of objects.');
            }
            return response.data;
        } catch (error) {
            let errorMsg = `Failed to load prompts for pipeline ${pipelineName}.`;
             if (error.response?.status === 401) { errorMsg = "Auth failed fetching pipeline."; if (handleLogout) handleLogout(); }
             else if (error.response?.status === 404) { errorMsg = `Pipeline '${pipelineName}' not found.`; }
             else { errorMsg += ` ${error.message}`; }
            setError(prev => `${prev ? prev + ' | ' : ''}${errorMsg}`);
            return [];
        }
    }, [BASE_URL, handleLogout]);

    const fetchAttachedFileContent = useCallback(async (filePaths) => {
        if (!filePaths || filePaths.length === 0) return '';
        try {
            const response = await axios.post(`${BASE_URL}/api/read_user_files`, { filePaths }, { withCredentials: true });
            if (response.data?.errors?.length > 0) {
                 setError(prev => `${prev ? prev + ' | ' : ''}Warning: Could not read some files. ${response.data.errors.join(', ')}`);
            }
            return response.data?.combinedContent || '';
        } catch (error) {
            let errorMsg = "Failed to load content for attached files.";
            if (error.response?.status === 401) errorMsg += " Unauthorized.";
            else if (error.response?.data?.error) errorMsg = error.response.data.error;
            else if (error.message) errorMsg += ` ${error.message}.`
            setError(prev => `${prev ? prev + ' | ' : ''}${errorMsg}`);
            return '';
        }
    }, [BASE_URL]);

    // --- Main Send Handler ---
    const handleSend = useCallback(async () => {
        if (!input.trim() || loading || showCollectionPopup || showDbPopup) return;

        const userMessageContentBase = input.trim();
        let userMessageContentToSend = userMessageContentBase;

        let tempMessages = [...messages];
        let effectiveChatIdForAPI = currentChatId;
        let forkedFromHistory = false;

        if (selectedChat && selectedChat.id) {
            console.log(`Forking from selected historical chat ${selectedChat.id}. This will become a new chat session upon saving.`);
            setOriginalLoadedMessages([...tempMessages]);
            setSelectedChat(null);
            setCurrentChatId(null);
            effectiveChatIdForAPI = null;
            forkedFromHistory = true;
            setPausedAgentStepConversation(null);
            setPausedAgentStepIndex(-1);
            setPausedPipelineName('');
        }

        let systemPromptAddition = '';
        let filesUsedInSend = [...attachedFiles];
        let operationSucceeded = false;

        setError(''); setLoading(true); setInput('');

        if (!useAgentAPI || !selectedPipeline || (selectedPipeline && selectedPipeline !== pausedPipelineName)) {
            if (pausedAgentStepConversation) {
                console.log("New operation or different pipeline selected, clearing previously paused agent step.");
                setPausedAgentStepConversation(null);
                setPausedAgentStepIndex(-1);
                setPausedPipelineName('');
            }
        }

        if (filesUsedInSend.length > 0) {
            if (useAgentAPI) {
                const formattedPaths = filesUsedInSend.map(p => `- /Home/${p}`).join('\n');
                userMessageContentToSend = `Attached Files:\n${formattedPaths}\n\n${userMessageContentBase}`;
            } else {
                const combinedContent = await fetchAttachedFileContent(filesUsedInSend);
                if (combinedContent) {
                    systemPromptAddition = `\n\n--- Attached File Content ---\n${combinedContent}\n--- End Attached File Content ---`;
                } else {
                    filesUsedInSend = [];
                }
            }
        }

        const userMessage = { role: 'user', content: userMessageContentToSend };
        tempMessages.push(userMessage);
        setMessages(tempMessages);

        const messageToSendForNonAgent = userMessageContentBase;
        
        console.log("handleSend: Current systemPrompt state:", JSON.stringify(systemPrompt, null, 2));
        const selectedPromptText = typeof systemPrompt === 'object' ? (systemPrompt?.text || '') : (systemPrompt || '');
        const selectedPromptName = (typeof systemPrompt === 'object' && systemPrompt.name !== undefined) 
                            ? systemPrompt.name 
                            : null;
        console.log("handleSend: Extracted selectedPromptName:", selectedPromptName);

        const effectiveSystemPromptText = selectedPromptText + systemPromptAddition;

        const currentProvider = userLLMSettings?.llmprovider || settings.llmProvider;
        const historyForAPI = tempMessages.slice(0, -1);

        // MODIFIED: Declare before try block to ensure scope for catch block
        let accumulatedPipelineMessages = [...tempMessages]; 

        try {
            let responseData = {};
            if (useAgentAPI && selectedPipeline) {
                // --- PIPELINE AGENT LOGIC ---
                console.log(`Starting Agent Pipeline: ${selectedPipeline}`);
                
                // MODIFIED: Declare missing local variable
                let pipelineSuccessfullyCompletedAllSteps = true; 

                let pipelinePrompts = [];
                try {
                    console.log(`We made it to part 1`);
                    pipelinePrompts = await fetchPipelinePrompts(selectedPipeline);
                } catch (fetchErr) {
                    setError(`Failed to load pipeline '${selectedPipeline}': ${fetchErr.message}`);
                    setLoading(false);
                    setMessages(prev => prev.slice(0, -1)); 
                    return;
                }

                if (!pipelinePrompts || pipelinePrompts.length === 0) {
                    setError(`Pipeline '${selectedPipeline}' is empty or failed to load.`);
                    setLoading(false);
                    setMessages(prev => prev.slice(0, -1));
                    return;
                }

                let currentPipelineInputForNextAgent = userMessageContentToSend; 
                // The original `let accumulatedPipelineMessages = [...tempMessages];` was moved outside the try block

                let pipelineStartIndex = 0;
                if (pausedAgentStepConversation && pausedPipelineName === selectedPipeline && pausedAgentStepIndex >= 0) {
                    pipelineStartIndex = pausedAgentStepIndex; 
                    currentPipelineInputForNextAgent = userMessageContentToSend; 
                    console.log(`Resuming pipeline '${selectedPipeline}' from paused step ${pipelineStartIndex + 1}.`);
                } else {
                    setPausedAgentStepConversation(null);
                    setPausedAgentStepIndex(-1);
                    setPausedPipelineName('');
                }

                for (let i = pipelineStartIndex; i < pipelinePrompts.length; i++) {
                    const basePipelinePrompt = pipelinePrompts[i];
                    const basePipelinePromptText = basePipelinePrompt.prompt_text || "No prompt text provided for this step.";
                    const basePipelinePromptName = basePipelinePrompt.prompt_name || `Pipeline Step ${i + 1}`;
                    
                    const completionInstruction = "<<AGENT COMPLETE>>"; 
                    let currentPipelineStepSystemPromptText = basePipelinePromptText;
                    if (!currentPipelineStepSystemPromptText.toLowerCase().includes(completionInstruction.toLowerCase())) {
                        currentPipelineStepSystemPromptText += `\n\n(When your task for this step is fully complete, you MUST include the exact phrase "${completionInstruction}" in your final message for this step.)`;
                    }
                    const currentPipelineStepSystemPromptName = basePipelinePromptName;
                    
                    const pipelineStepStartMessage = { 
                        role: 'system', 
                        content: `--- Starting Pipeline: ${selectedPipeline} - Step ${i + 1}/${pipelinePrompts.length} ---\nUsing Prompt: "${currentPipelineStepSystemPromptText.substring(0, 150)}..."` 
                    };
                    accumulatedPipelineMessages.push(pipelineStepStartMessage);
                    setMessages([...accumulatedPipelineMessages]);

                    if (!(pausedAgentStepConversation && pausedAgentStepIndex === i && pausedPipelineName === selectedPipeline)) {
                        const pipelineStepUiStartMessage = { role: 'system', content: `--- Starting Pipeline: ${selectedPipeline} - Step ${i + 1}/${pipelinePrompts.length} ---\nUsing Prompt: "${basePipelinePromptName}"` };
                        // MODIFIED: Use the declared variable `accumulatedPipelineMessages`
                        accumulatedPipelineMessages.push(pipelineStepUiStartMessage);
                        setMessages([...accumulatedPipelineMessages]);
                    }

                    let currentAgentStepConversation;
                    if (pausedAgentStepConversation && pausedAgentStepIndex === i && pausedPipelineName === selectedPipeline) {
                        currentAgentStepConversation = [...pausedAgentStepConversation, { role: 'user', content: userMessageContentToSend }];
                        console.log(`Resuming agent step ${i+1} with its previous conversation and new user input.`);
                        setPausedAgentStepConversation(null); 
                        setPausedAgentStepIndex(-1);
                    } else {
                        currentAgentStepConversation = [{ role: 'user', content: currentPipelineInputForNextAgent }];
                    }

                    // --- REFACTORED LOGIC: REMOVED THE CLIENT-SIDE `while` LOOP ---
                    // Make a single call to the backend. The backend will handle the `loopsBeforeHumanInput`.
                    console.log(`Pipeline "${selectedPipeline}", Step ${i+1}: Calling agent. Backend will loop up to ${loopsBeforeHumanInput} times.`);

                    const agentStepResponse = await attemptRequest(
                        currentAgentStepConversation, currentProvider, selectedTools,
                        effectiveChatIdForAPI, currentPipelineStepSystemPromptText, basePipelinePromptName 
                    );

                    if (!agentStepResponse.output || !Array.isArray(agentStepResponse.output)) {
                        throw new Error('Invalid agent response format received from backend for pipeline step.');
                    }
                    
                    // The entire conversation for this step is returned from the backend.
                    const finalConversationForStep = agentStepResponse.output;
                    const displayableMessagesForStep = finalConversationForStep.filter(
                        (msg, idx) => !(idx === 0 && msg.role === 'system' && msg.content === currentPipelineStepSystemPromptText)
                    );
                    
                    // MODIFIED: Use the declared variable `accumulatedPipelineMessages`
                    setMessages([...accumulatedPipelineMessages, ...displayableMessagesForStep]);
                    accumulatedPipelineMessages.push(...displayableMessagesForStep); // Also update the master message list for the next step

                    const lastMessageFromAgent = finalConversationForStep[finalConversationForStep.length - 1];
                    let lastAgentMessageContentForThisStep = lastMessageFromAgent?.content || "";

                    const agentStepCompletedBySignal = lastMessageFromAgent?.role === 'assistant' && lastAgentMessageContentForThisStep.includes(completionInstruction);
                    
                    // --- Check if the step completed or was paused by the backend loop limit ---
                    if (agentStepCompletedBySignal) {
                        console.log(`Pipeline Step ${i+1} ("${basePipelinePromptName}") signaled completion.`);
                        lastAgentMessageContentForThisStep = lastAgentMessageContentForThisStep.replace(completionInstruction, "").trim();
                        // Update the last message to remove the signal before it's used as input for the next step
                        if(accumulatedPipelineMessages.length > 0) {
                            accumulatedPipelineMessages[accumulatedPipelineMessages.length - 1].content = lastAgentMessageContentForThisStep;
                        }
                        
                        // Clear any lingering paused state for this step
                        if (pausedAgentStepIndex === i && pausedPipelineName === selectedPipeline) {
                            setPausedAgentStepConversation(null);
                            setPausedAgentStepIndex(-1);
                            setPausedPipelineName('');
                        }

                        // If this was the last step, the pipeline is done.
                        if (i === pipelinePrompts.length - 1) {
                             console.log(`Pipeline finished. Final output from last agent step is ready.`);
                        }

                    } else { // Agent did NOT signal completion, meaning it was paused by the backend's loop limit.
                        const pauseMessageFromBackend = lastMessageFromAgent?.role === 'system' && lastMessageFromAgent?.content.includes("PAUSED:");
                        const pauseReason = pauseMessageFromBackend ? lastMessageFromAgent.content : `--- Pipeline Step ${i+1} paused after ${loopsBeforeHumanInput} turns. Provide more input to continue. ---`;
                        
                        // We might not need to add another message if the backend already added one.
                        if (!pauseMessageFromBackend) {
                            // MODIFIED: Use the declared variable `accumulatedPipelineMessages`
                            accumulatedPipelineMessages.push({ role: 'system', content: pauseReason });
                            setMessages([...accumulatedPipelineMessages]);
                        }
                        
                        setError(`Pipeline paused: Step "${basePipelinePromptName}" did not complete.`);
                        pipelineSuccessfullyCompletedAllSteps = false; 
                        setPausedAgentStepConversation(finalConversationForStep); // Store the full history of the paused step
                        setPausedAgentStepIndex(i);
                        setPausedPipelineName(selectedPipeline);
                        setLoading(false);
                        return; // Stop handleSend here, wait for next user interaction
                    }

                    // --- Summarization Logic (if step completed and it's not the last step) ---
                    if (agentStepCompletedBySignal && i < pipelinePrompts.length - 1) { 
                        const summarySystemPrompt = `You are an expert executive assistant. Summarize the following interaction to guide the next agent. Focus on key findings, generated files, and information relevant to the original user query.`;
                        const previousAgentFullInteractionForSummary = finalConversationForStep.map(msg => `### ${msg.role.toUpperCase()} ###\n${msg.content}`).join('\n\n---\n\n');
                        const messagesForSummarizer = [
                            { role: 'system', content: summarySystemPrompt },
                            { role: 'user', content: `Original User Query:\n${userMessageContentBase}\n\nInteraction to Summarize:\n${previousAgentFullInteractionForSummary}` }
                        ];
                        const summaryChatResponse = await axios.post(`${BASE_URL}/api/prochat`, { messages: messagesForSummarizer }, { timeout: FIVE_MINUTES_TIMEOUT, withCredentials: true });
                        if (summaryChatResponse.data?.choices?.[0]?.message?.content) {
                            const summaryForNextAgent = summaryChatResponse.data.choices[0].message.content.trim();
                            currentPipelineInputForNextAgent = `Original User Query:\n${userMessageContentBase}\n\nSummary of Previous Agent's Work:\n${summaryForNextAgent}`;
                            const summaryMessageForDisplay = { role: 'system', content: `--- Summary of Step ${i + 1} (for Next Agent) ---\n${summaryForNextAgent}` };
                            // MODIFIED: Use the declared variable `accumulatedPipelineMessages`
                            accumulatedPipelineMessages.push(summaryMessageForDisplay);
                            setMessages([...accumulatedPipelineMessages]); 
                        } else {
                            const summarizationErrorMessage = `--- Error: Failed to summarize output of Step ${i+1}. Pipeline cannot continue. ---`;
                            // MODIFIED: Use the declared variable `accumulatedPipelineMessages`
                            accumulatedPipelineMessages.push({ role: 'system', content: summarizationErrorMessage });
                            setMessages([...accumulatedPipelineMessages]);
                            setError(`Pipeline error: Summarization failed for step "${basePipelinePromptName}".`);
                            pipelineSuccessfullyCompletedAllSteps = false;
                            break; 
                        }
                    }
                } 

                if (pipelineSuccessfullyCompletedAllSteps) {
                    operationSucceeded = true;
                    setPausedAgentStepConversation(null);
                    setPausedAgentStepIndex(-1);
                    setPausedPipelineName('');
                } else {
                    operationSucceeded = false; 
                }
                
                setMessages(accumulatedPipelineMessages);
                setOriginalLoadedMessages([...accumulatedPipelineMessages]); 
                operationSucceeded = true;

            } else if (useAgentAPI) { 
                if (pausedAgentStepConversation) {
                    setPausedAgentStepConversation(null);
                    setPausedAgentStepIndex(-1);
                    setPausedPipelineName('');
                }
                console.log("Using Single Agent API. The backend will loop up to", loopsBeforeHumanInput, "times.");
                const agentSystemPromptTextForSingleAgent = effectiveSystemPromptText || 'You are a helpful agent.';
                const agentResponse = await attemptRequest(
                    tempMessages, // Single agent calls use the simpler `tempMessages` array
                    currentProvider,
                    selectedTools,
                    effectiveChatIdForAPI,
                    agentSystemPromptTextForSingleAgent, 
                    selectedPromptName // Pass the name of the system prompt selected in settings
                );
                
                if (agentResponse.output && Array.isArray(agentResponse.output)) {
                    setMessages(agentResponse.output); 
                    setOriginalLoadedMessages([...agentResponse.output]);
                } else {
                    throw new Error('Invalid agent response format.');
                }

            } else { // Non-Agent Logic
                if (pausedAgentStepConversation) {
                    setPausedAgentStepConversation(null);
                    setPausedAgentStepIndex(-1);
                    setPausedPipelineName('');
                }
                const commonConfig = { timeout: FIVE_MINUTES_TIMEOUT, withCredentials: true };
                const baseMessagesForCall = [ ...(effectiveSystemPromptText ? [{ role: 'system', content: effectiveSystemPromptText }] : []), ...historyForAPI, { role: 'user', content: messageToSendForNonAgent } ];
                let finalAssistantMessageContent = '';

                if (useText2SQL) {
                    if (!selectedDb) throw new Error('Text2SQL enabled, but no database selected.');
                    const t2sPayload = { userQuery: messageToSendForNonAgent, dbSelection: selectedDb, chatId: effectiveChatIdForAPI };
                    const response = await axios.post(`${BASE_URL}/api/text2sql`, t2sPayload, commonConfig);
                    responseData = response.data;
                    const executedQuery = responseData.query || 'N/A'; const resultsJson = responseData.results || '[]'; let resultsDisplay = '(No results)'; try { const d = JSON.parse(resultsJson); if(Array.isArray(d) && d.length>0){ const h = Object.keys(d[0]); resultsDisplay = `| ${h.join(' | ')} |\n| ${h.map(()=>'---').join(' | ')} |\n${d.map(r=>`| ${h.map(k=>String(r[k]??'').replace(/\|/g,'\\|')).join(' | ')} |`).join('\n')}`; } else if(Array.isArray(d)){ resultsDisplay = '(0 rows returned)';}} catch(e){ console.error("Error parsing T2S results JSON", e);} const qf = responseData.queryFile ? `\n\n*Results saved: ${responseData.queryFile}*` : '';
                    finalAssistantMessageContent = `**Text2SQL Result (DB: \`${selectedDb}\`)**\n\n**Executed Query:**\n\`\`\`sql\n${executedQuery}\n\`\`\`\n**Results:**\n${resultsDisplay}${qf}`;
                } else if (useRAG) {
                    const ragPayload = { userInput: messageToSendForNonAgent, messages: historyForAPI, systemPrompt: effectiveSystemPromptText, selectedCollectionName: selectedKnowledgeName, chat_id: effectiveChatIdForAPI };
                    const response = await axios.post(`${BASE_URL}/api/rag-chat`, ragPayload, commonConfig);
                    responseData = response.data;
                    finalAssistantMessageContent = responseData.content;
                } else {
                    // This is the standard prochat call. It will use the userLLMSettings for provider, model, temp, tokens.
                    // The system prompt is already prepended to baseMessagesForCall if effectiveSystemPromptText exists.
                    const proChatPayload = { messages: baseMessagesForCall, chat_id: effectiveChatIdForAPI };
                    const response = await axios.post(`${BASE_URL}/api/prochat`, proChatPayload, commonConfig);
                    responseData = response.data;
                    finalAssistantMessageContent = responseData.choices?.[0]?.message?.content || JSON.stringify(responseData);
                }
                 setMessages(prev => [...prev, { role: 'assistant', content: finalAssistantMessageContent }]);
            }
            operationSucceeded = true;

            setMessages(currentMessagesAfterResponse => {
                 setOriginalLoadedMessages([...currentMessagesAfterResponse]);
                 return currentMessagesAfterResponse;
            });

        } catch (err) {
            console.error('Error caught in handleSend:', err);
            if (!error) { setError(err.message || "An unexpected error occurred."); }
            // MODIFIED: Use the correctly scoped variable `accumulatedPipelineMessages`
            const messagesBeforeUserSend = accumulatedPipelineMessages.slice(0, accumulatedPipelineMessages.findIndex(m => m === userMessage));
            setMessages(messagesBeforeUserSend.length > 0 ? messagesBeforeUserSend : []);
            if (forkedFromHistory) {
                 setSelectedChat({ id: currentChatId, /* Need original chat data here */ });
                 setCurrentChatId(currentChatId);
                 setMessages([...originalLoadedMessages]);
            }
        } finally {
             if (operationSucceeded && filesUsedInSend.length > 0 && attachedFiles.length > 0) {
                 setAttachedFiles([]); 
             }
             
            const isPipelinePausedForUserInput = pausedAgentStepConversation !== null;

            if (isPipelinePausedForUserInput) {
                // setLoading(false) was already called inside the loop before `return;`.
                console.log("handleSend finished: Pipeline is paused and waiting for user input. UI is active.");
            } else {
                console.log("handleSend finished: Request complete for this turn. Unlocking UI.");
                setLoading(false);
            }
        }
    }, [
        input, loading, showCollectionPopup, showDbPopup, messages, attachedFiles, selectedChat, currentChatId,
        useAgentAPI, fetchAttachedFileContent, systemPrompt, userLLMSettings, settings.llmProvider,
        selectedPipeline, selectedTools, loopsBeforeHumanInput, 
        fetchPipelinePrompts, attemptRequest, handleLogout, useText2SQL, selectedDb,
        useRAG, selectedKnowledgeName, error, originalLoadedMessages,
        pausedAgentStepConversation, pausedAgentStepIndex, pausedPipelineName,
    ]);

    // --- Popup Handlers ---
    const handleInputChange = (event) => {
        const currentInput = event.target.value;
        const cursorPos = event.target.selectionStart;
        setInput(currentInput);

        const collectionTrigger = "||";
        const dbTrigger = "|\\";
        let triggerDetected = false;

        const dbTriggerStart = currentInput.lastIndexOf(dbTrigger, cursorPos - dbTrigger.length);
        if (dbTriggerStart !== -1 && dbTriggerStart === cursorPos - dbTrigger.length) {
            triggerDetected = true; setShowCollectionPopup(false);
            const filter = currentInput.substring(dbTriggerStart + dbTrigger.length, cursorPos);
            setDbFilter(filter); setDbTriggerIndex(dbTriggerStart);
            if (!showDbPopup) {
                if (inputBarRef.current) { const rect = inputBarRef.current.getBoundingClientRect(); setPopupPosition({ bottom: window.innerHeight - rect.top - window.scrollY + 5, left: rect.left + window.scrollX, width: rect.width });}
                setShowDbPopup(true);
            }
        } else if (showDbPopup) {
            if (cursorPos >= dbTriggerIndex + dbTrigger.length && currentInput.startsWith(dbTrigger, dbTriggerIndex)) {
                setDbFilter(currentInput.substring(dbTriggerIndex + dbTrigger.length, cursorPos)); triggerDetected = true;
            } else { setShowDbPopup(false); setDbFilter(''); setDbTriggerIndex(-1); }
        }

        if (!triggerDetected || (triggerDetected && !showDbPopup)) {
            const collectionTriggerStart = currentInput.lastIndexOf(collectionTrigger, cursorPos - collectionTrigger.length);
            if (collectionTriggerStart !== -1 && collectionTriggerStart === cursorPos - collectionTrigger.length) {
                triggerDetected = true; setShowDbPopup(false);
                const filter = currentInput.substring(collectionTriggerStart + collectionTrigger.length, cursorPos);
                setCollectionFilter(filter); setCollectionTriggerIndex(collectionTriggerStart);
                if (!showCollectionPopup) {
                    if (inputBarRef.current) { const rect = inputBarRef.current.getBoundingClientRect(); setPopupPosition({ bottom: window.innerHeight - rect.top - window.scrollY + 5, left: rect.left + window.scrollX, width: rect.width });}
                    setShowCollectionPopup(true);
                }
            } else if (showCollectionPopup) {
                if (cursorPos >= collectionTriggerIndex + collectionTrigger.length && currentInput.startsWith(collectionTrigger, collectionTriggerIndex)) {
                    setCollectionFilter(currentInput.substring(collectionTriggerIndex + collectionTrigger.length, cursorPos)); triggerDetected = true;
                } else { setShowCollectionPopup(false); setCollectionFilter(''); setCollectionTriggerIndex(-1); }
            }
        }
        if (!triggerDetected) {
            if (showCollectionPopup) { setShowCollectionPopup(false); }
            if (showDbPopup) { setShowDbPopup(false); }
        }
    };
    const handleCollectionSelect = (selectedName) => {
        if (collectionTriggerIndex === -1) return;
        const currentInput = inputRef.current.value; const triggerSequence = "||";
        const textBefore = currentInput.substring(0, collectionTriggerIndex);
        const filterEndIndex = collectionTriggerIndex + triggerSequence.length + collectionFilter.length;
        const textAfter = currentInput.substring(filterEndIndex);
        const newInput = `${textBefore}${selectedName}${textAfter}`;
        setInput(newInput); setShowCollectionPopup(false); setCollectionFilter(''); setCollectionTriggerIndex(-1);
        setTimeout(() => { if (inputRef.current) { inputRef.current.focus(); const newCursorPos = collectionTriggerIndex + selectedName.length; inputRef.current.setSelectionRange(newCursorPos, newCursorPos);}}, 0);
    };
    const handleDbSelect = (selectedName) => {
        if (dbTriggerIndex === -1) return;
        const currentInput = inputRef.current.value; const triggerSequence = "|\\";
        const textBefore = currentInput.substring(0, dbTriggerIndex);
        const filterEndIndex = dbTriggerIndex + triggerSequence.length + dbFilter.length;
        const textAfter = currentInput.substring(filterEndIndex);
        const newInput = `${textBefore}${selectedName}${textAfter}`;
        setInput(newInput); setShowDbPopup(false); setDbFilter(''); setDbTriggerIndex(-1);
        setTimeout(() => { if (inputRef.current) { inputRef.current.focus(); const newCursorPos = dbTriggerIndex + selectedName.length; inputRef.current.setSelectionRange(newCursorPos, newCursorPos);}}, 0);
    };
    const handleInputBlur = () => {
        setTimeout(() => {
            if (!document.activeElement?.closest('.input-suggestion-popup')) {
                setShowCollectionPopup(false); setShowDbPopup(false);
            }
        }, 150);
    };
    const filteredCollections = Array.isArray(collectionNames) ? collectionNames.filter(name => name.toLowerCase().includes(collectionFilter.toLowerCase())) : [];
    const filteredDbs = Array.isArray(uniqueRemoteDbs) ? uniqueRemoteDbs.filter(name => name.toLowerCase().includes(dbFilter.toLowerCase())) : [];

    // --- Explorer Log Modal ---
    // handleUUIDClick now directly uses state setters from ChatWindow
    // const handleUUIDClick = useCallback(async (uuid) => {
    //     if (!uuid) return;
    //     console.log(`UUID clicked: ${uuid}`);
    //     setSelectedExplorerUUID(uuid);
    //     setExplorerModalError('');
    //     setExplorerLogData(null);
    //     setIsExplorerModalLoading(true);
    //     setIsExplorerModalOpen(true); // Open modal immediately
    //     try {
    //         const response = await axios.get(`${BASE_URL}/api/load_log/${uuid}`, { withCredentials: true });
    //         if (response.data) {
    //             console.log("Fetched explorer log data successfully.");
    //             setExplorerLogData(response.data);
    //         } else {
    //             throw new Error("No data received from log endpoint.");
    //         }
    //     } catch (error) {
    //         let msg = `Failed load log ${uuid.substring(0, 8)}...`;
    //         if (error.response?.status === 401) {
    //             msg += " Unauthorized.";
    //             // Optionally trigger logout if auth fails
    //             // if (handleLogout) handleLogout(); 
    //         } else if (error.response?.status === 404) {
    //             msg = `Log not found: ${uuid.substring(0, 8)}...`;
    //         } else if (error.response?.data?.error) {
    //             msg += ` ${error.response.data.error}`;
    //         } else if (error.message) {
    //             msg += ` ${error.message}`;
    //         }
    //         console.error(`Error fetching log data for UUID ${uuid}:`, error);
    //         setExplorerModalError(msg);
    //     } finally {
    //         setIsExplorerModalLoading(false);
    //     }
    // }, [BASE_URL, handleLogout]); // Add handleLogout if it's used within error handling

    // const processNodeForUUID = useCallback((node) => {
    //     if (typeof node === 'string') {
    //         const uuidRegex = /@@([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})@@/g;
    //         const parts = [];
    //         let lastIndex = 0;
    //         let match;

    //         while ((match = uuidRegex.exec(node)) !== null) {
    //             // Add text before the UUID
    //             if (match.index > lastIndex) {
    //                 parts.push(node.substring(lastIndex, match.index));
    //             }
    //             // Add the clickable UUID
    //             const uuid = match[1];
    //             parts.push(
    //                 <span
    //                     key={`${match.index}-${uuid}`}
    //                     className="clickable-uuid" // Defined in ChatWindow.css or App.css
    //                     onClick={(e) => {
    //                         e.stopPropagation(); // Prevent triggering other click listeners if nested
    //                         handleUUIDClick(uuid);
    //                     }}
    //                     title={`View Exploration Log (${uuid})`}
    //                 >
    //                     {`«Explore: ${uuid.substring(0, 8)}...»`} 
    //                 </span>
    //             );
    //             lastIndex = uuidRegex.lastIndex;
    //         }

    //         // Add any remaining text after the last UUID
    //         if (lastIndex < node.length) {
    //             parts.push(node.substring(lastIndex));
    //         }

    //         // If parts were added, return them wrapped in a fragment, otherwise return original node
    //         return parts.length > 0 ? <>{parts}</> : node;
    //     } else if (React.isValidElement(node) && node.props && node.props.children) {
    //         // Recursively process children of React elements
    //         return React.cloneElement(node, {
    //             ...node.props,
    //             children: React.Children.map(node.props.children, child => processNodeForUUID(child))
    //         });
    //     }
    //     // Return the node unchanged if it's not a string or a React element with children
    //     return node;
    // }, [handleUUIDClick]); // Ensure handleUUIDClick is a dependency


    // --- Explorer Log Modal ---
    const handleUUIDClick = useCallback(async (uuid) => {
        if (!uuid) return;
        console.log(`UUID clicked: ${uuid}`);
        setSelectedExplorerUUID(uuid); setExplorerModalError(''); setExplorerLogData(null);
        setIsExplorerModalLoading(true); setIsExplorerModalOpen(true);
        try {
            const response = await axios.get(`${BASE_URL}/api/load_log/${uuid}`, { withCredentials: true });
            if (response.data) {
                 console.log("Fetched explorer log data successfully.");
                 setExplorerLogData(response.data);
            } else { throw new Error("No data received from log endpoint."); }
        } catch (error) {
            let msg = `Failed load log ${uuid.substring(0,8)}...`;
            if (error.response?.status === 401) msg += " Unauthorized.";
            else if (error.response?.status === 404) msg = `Log not found: ${uuid.substring(0,8)}...`;
            else if (error.response?.data?.error) msg += ` ${error.response.data.error}`;
            else if (error.message) msg += ` ${error.message}`;
            console.error(`Error fetching log data for UUID ${uuid}:`, error);
            setExplorerModalError(msg);
        } finally { setIsExplorerModalLoading(false); }
    }, [BASE_URL]);

    // --- Markdown Rendering Logic ---
    const processNodeForUUID = useCallback((node) => {
        if (typeof node === 'string') {
            const uuidRegex = /@@([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})@@/g;
            const parts = []; let lastIndex = 0; let match;
            while ((match = uuidRegex.exec(node)) !== null) {
                if (match.index > lastIndex) { parts.push(node.substring(lastIndex, match.index)); }
                const uuid = match[1];
                parts.push(
                    <span
                        key={`${match.index}-${uuid}`} 
                        className="clickable-uuid" 
                        onClick={(e) => { e.stopPropagation(); handleUUIDClick(uuid); }} 
                        title={`View Exploration Log (${uuid})`} 
                    >
                        {`«Explore: ${uuid.substring(0, 8)}...»`} 
                    </span>
                );
                lastIndex = uuidRegex.lastIndex;
            }
            if (lastIndex < node.length) { parts.push(node.substring(lastIndex)); }
            return parts.length > 0 ? <>{parts}</> : node;
        } else if (React.isValidElement(node) && node.props.children) {
            return React.cloneElement(node, { ...node.props, children: React.Children.map(node.props.children, processNodeForUUID) });
        }
        return node;
    }, [handleUUIDClick]); // Add handleUUIDClick if it's a dependency from the component scope
    
    const markdownOptions = useMemo(() => ({
        overrides: {
            // Process standard text containers for UUIDs (Existing - Keep As Is)
            p: { component: ({ children, ...props }) => <p {...props}>{React.Children.map(children, processNodeForUUID)}</p> },
            pre: { component: ({ children, ...props }) => <pre {...props}>{React.Children.map(children, processNodeForUUID)}</pre> },
            code: { component: ({ children, ...props }) => <code {...props}>{React.Children.map(children, processNodeForUUID)}</code> },
            li: {
                component: ({ children, ...props }) => (
                    <li {...props}>{React.Children.map(children, child => processNodeForUUID(child))}</li>
                )
            },
            span: { 
                component: ({ children, ...props }) => (
                    <span {...props}>{React.Children.map(children, child => processNodeForUUID(child))}</span>
                )
            },
            // --- MODIFIED 'a' (anchor) TAG OVERRIDE ---
            a: {
                component: ({ children, href, ...props }) => {
                    if (!href) { 
                        return <a href={href} {...props}>{React.Children.map(children, child => processNodeForUUID(child))}</a>;
                    }
    
                    let normalizedHrefForApi = href;
                    if (href.startsWith('./')) {
                        normalizedHrefForApi = href.substring(2); 
                    }
    
                    const isAgentManagedPath = normalizedHrefForApi.startsWith('Home/') || normalizedHrefForApi.startsWith('charts/');
    
                    if (isAgentManagedPath && authUsername) {
                        const filename = normalizedHrefForApi.substring(normalizedHrefForApi.lastIndexOf('/') + 1);
                        const fileType = filename.split('.').pop()?.toLowerCase() || '';
                        
                        let pathRelativeToHome = '';
                        if (normalizedHrefForApi.startsWith('Home/')) {
                            pathRelativeToHome = normalizedHrefForApi.substring('Home/'.length);
                        }
                        // For legacy 'charts/' paths, pathRelativeToHome will be empty.
                        // DocumentViewerModal relies on paths relative to Home.
    
                        const isDocx = fileType === 'docx';
                        const isPdf = fileType === 'pdf';
                        const isCsv = fileType === 'csv';
                        const isTxt = fileType === 'txt';
                        const isTargetDocumentTypeForModal = (isDocx || isPdf || isCsv || isTxt) && pathRelativeToHome;
                        
                        const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.ico'];
                        const isImage = imageExtensions.some(ext => normalizedHrefForApi.toLowerCase().endsWith(ext));
                        
                        const apiUrl = `${BASE_URL}/api/agent_files/${authUsername}/${normalizedHrefForApi}`;
    
                        if (isImage && pathRelativeToHome) { // Link to an image under Home/, make it open in viewer
                            const altTextContent = children ? (Array.isArray(children) ? children.join('') : String(children)) : filename;
                            return (
                                 <img 
                                    src={apiUrl}
                                    alt={altTextContent || filename}
                                    className="agent-generated-image" // This class should have `cursor: pointer;`
                                    title={`View Image: ${filename} (Click to open in viewer)`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log(`Linked image click: Opening DocumentViewerModal for path relative to Home: ${pathRelativeToHome}`);
                                        setViewingFilePath(pathRelativeToHome);
                                        setIsViewerOpen(true);
                                    }}
                                    onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                                    // Do not spread {...props} from <a> to <img> unless specific props are intended
                                />
                            );
                        } else if (isImage) { // Link to an image NOT under Home/ (e.g. legacy charts/)
                            const altTextContent = children ? (Array.isArray(children) ? children.join('') : String(children)) : filename;
                            return ( // Display image, but link to its raw API URL (opens in new tab)
                                <a href={apiUrl} target="_blank" rel="noopener noreferrer" title={`View Image: ${filename} (Opens in new tab)`}>
                                    <img src={apiUrl} alt={altTextContent} className="agent-generated-image" onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }} />
                                </a>
                            );
                        } else if (isTargetDocumentTypeForModal) { // Existing logic for other documents
                            const linkTextContent = children ? React.Children.map(children, child => processNodeForUUID(child)) : filename;
                            return (
                                <a
                                    href={apiUrl} 
                                    onClick={(e) => {
                                        e.preventDefault(); 
                                        console.log(`Opening DocumentViewerModal for: ${pathRelativeToHome} (Original href: ${href})`);
                                        setViewingFilePath(pathRelativeToHome);
                                        setIsViewerOpen(true);
                                    }}
                                    title={`View Document: ${filename}`}
                                    {...props}
                                    style={{ cursor: 'pointer', color: 'var(--info-color)', ...props.style }}
                                >
                                    {linkTextContent}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" style={{ marginLeft: '4px', verticalAlign: 'middle' }}>
                                      <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0"/>
                                      <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7"/>
                                    </svg>
                                </a>
                            );
                        } else { // Default to download link for other agent-managed files
                            const linkTextContent = children ? React.Children.map(children, child => processNodeForUUID(child)) : filename;
                            return (
                                <a href={apiUrl} target="_blank" rel="noopener noreferrer" title={`Download File: ${filename}`} {...props}>
                                    {linkTextContent}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" style={{ marginLeft: '4px', verticalAlign: 'middle' }}>
                                        <path fillRule="evenodd" d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
                                        <path d="M1 14.5a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 14.5"/>
                                    </svg>
                                </a>
                            );
                        }
                    } else if (isAgentManagedPath && !authUsername) { 
                        const filename = normalizedHrefForApi.substring(normalizedHrefForApi.lastIndexOf('/') + 1);
                        return <span title="Cannot display file: Authentication required. Please log in.">[File: {filename}]</span>;
                    }
                    
                    return <a href={href} {...props}>{React.Children.map(children, child => processNodeForUUID(child))}</a>;
                }
            },
            // --- MODIFIED 'img' TAG OVERRIDE ---
            img: { 
                component: ({ src, alt, ...props }) => {
                    if (!src) return <img src={src} alt={alt} {...props} />;
    
                    let normalizedSrcForApi = src;
                    if (src.startsWith('./')) {
                        normalizedSrcForApi = src.substring(2);
                    }
    
                    const isAgentManagedPath = normalizedSrcForApi.startsWith('Home/') || normalizedSrcForApi.startsWith('charts/');
    
                    if (isAgentManagedPath && authUsername) {
                        const apiUrl = `${BASE_URL}/api/agent_files/${authUsername}/${normalizedSrcForApi}`;
                        const filename = normalizedSrcForApi.substring(normalizedSrcForApi.lastIndexOf('/') + 1);
    
                        // Check if the image is under the 'Home/' directory to make it clickable for the viewer
                        if (normalizedSrcForApi.startsWith('Home/')) {
                            const pathRelativeToHomeForViewer = normalizedSrcForApi.substring('Home/'.length);
                            return (
                                <img
                                    src={apiUrl}
                                    alt={alt || filename}
                                    className="agent-generated-image" // Ensure this class has `cursor: pointer;`
                                    title={`View Image: ${filename} (Click to open in viewer)`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log(`Direct image click: Opening DocumentViewerModal for path relative to Home: ${pathRelativeToHomeForViewer}`);
                                        setViewingFilePath(pathRelativeToHomeForViewer);
                                        setIsViewerOpen(true);
                                    }}
                                    onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                                    {...props} // Spread other props like style, width, height if any
                                />
                            );
                        } else { // Image is not under Home (e.g., legacy charts/) - just display, not clickable for viewer
                            return (
                                <img
                                    src={apiUrl}
                                    alt={alt || filename}
                                    className="agent-generated-image"
                                    title={`Image: ${filename}`} // Simpler title
                                    onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                                    {...props}
                                />
                            );
                        }
                    } else if (isAgentManagedPath && !authUsername) {
                        const filename = normalizedSrcForApi.substring(normalizedSrcForApi.lastIndexOf('/') + 1);
                        return <span title="Cannot display image: Authentication required. Please log in.">[Image: {alt || filename}]</span>;
                    }
                    // Default img rendering if not an agent-managed path or user not authenticated
                    return <img src={src} alt={alt} {...props} />;
                }
            },
            // Preserve table styling (Existing - Keep As Is)
            table: { component: 'table', props: { className: 'markdown-table' } },
            thead: { component: 'thead', props: { className: 'markdown-thead' } },
            tbody: { component: 'tbody', props: { className: 'markdown-tbody' } },
            tr: { component: 'tr', props: { className: 'markdown-tr' } },
            th: { component: 'th', props: { className: 'markdown-th' } },
            td: { component: 'td', props: { className: 'markdown-td' } },
        },
        forceBlock: true,
    }), [authUsername, BASE_URL, processNodeForUUID, setIsViewerOpen, setViewingFilePath]); // Dependencies for useMemo
    
    // --- Render ---
    const newChatButtonText = () => {
        if (!selectedChat && messages.length > 0) return '+ Save & New Chat';
        return '+ New Chat';
    };

    return (
        <div className="chat-window-container">
            <div className="chat-sidebar-wrapper">
                <ChatHistorySidebar
                    history={chatHistory}
                    onChatSelect={handleSelectChat}
                    activeChatId={currentChatId}
                    onNewChat={handleNewChat}
                    refreshHistory={() => setRefreshSidebarKey(k => k + 1)} 
                />
                <button onClick={handleNewChat} className="new-chat-button" disabled={loading} title={newChatButtonText()} >
                    {newChatButtonText()}
                </button>
                <DocumentViewerModal
                    isOpen={isViewerOpen}
                    onClose={() => {
                        setIsViewerOpen(false);
                        setViewingFilePath(null); // Clear the path when closing
                    }}
                    filePath={viewingFilePath} // This should be path relative to Home
                    username={authUsername}
                />
            </div>

            <div className="chat-main-area">
                <div className="chat-area" ref={chatAreaRef}>
                    {error && <div className="chat-error-message">{error}</div>}
                    {!loading && messages.length === 0 && !error && (
                        <div className="empty-chat-placeholder">
                            <h2>Pearlify Chat</h2>
                            <p>Select a past chat or start typing below.</p>
                            {userLLMSettings ? (
                                <p className="settings-info">
                                    Using: <strong>{userLLMSettings.llmprovider || 'N/A'}</strong> - {userLLMSettings.model || 'N/A'}
                                    (Temp: {typeof userLLMSettings.temperature === 'number' ? userLLMSettings.temperature.toFixed(1) : 'N/A'}
                                    , Max Tokens: {typeof userLLMSettings.max_tokens === 'number' ? userLLMSettings.max_tokens.toLocaleString() : 'N/A'})
                                </p>
                            ) : ( <p className="settings-info">LLM settings missing or not loaded.</p> )}
                        </div>
                    )}
                    <div className="messages-list">
                        {/* --- NEW: "Load More" Button logic based on filtered messages --- */}
                        {processedMessagesForDisplay.length > displayedMessages.length && (
                            <div style={{ textAlign: 'center', margin: '10px 0' }}>
                                <button onClick={loadMoreMessages} className="load-more-button" disabled={loading}>
                                    Load Older Messages ({processedMessagesForDisplay.length - displayedMessages.length} more)
                                </button>
                            </div>
                        )}

                        {/* Map over the virtualized and filtered `displayedMessages` with a stable key */}
                        {displayedMessages.map((msg, index) => (
                            <div key={`${currentChatId || 'new'}-${processedMessagesForDisplay.length - displayedMessages.length + index}`} className={`message-bubble ${msg.role}`}>
                                <div className="message-role">{msg.role === 'user' ? (authUsername || 'User') : msg.role}</div>
                                <div className="message-content">
                                    <Markdown options={markdownOptions}>{String(msg.content || '')}</Markdown>
                                </div>
                            </div>
                        ))}
                        {loading && <div className="loading-indicator">Assistant is thinking...</div>}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                <div className="chat-input-bar" ref={inputBarRef}>
                    {attachedFiles.length > 0 && (
                        <div className="attachment-count-indicator" title={`${attachedFiles.length} file(s) attached. View/clear in Settings.`} >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0z"/></svg>
                            <span>{attachedFiles.length}</span>
                        </div>
                     )}
                    <textarea
                        ref={inputRef} className="chat-input" value={input}
                        placeholder="Type message... (|| for collections, |\ for DBs, Shift+Enter for newline)"
                        onChange={handleInputChange} onBlur={handleInputBlur}
                        onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !loading && !showCollectionPopup && !showDbPopup) { event.preventDefault(); handleSend(); }}}
                        rows={Math.max(1, Math.min(input.split('\n').length, 5))} disabled={loading}
                    />
                    <div className="chat-buttons">
                        <button onClick={handleSend} disabled={loading || !input.trim() || showCollectionPopup || showDbPopup} className="chat-button send" title="Send Message" >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/></svg>
                        </button>
                        <button onClick={() => setIsChatSettingsOpen(true)} disabled={loading} className="chat-button settings" title="Chat Settings" >
                           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311a1.464 1.464 0 0 1 .872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1-.872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872 2.105l.34-.1c1.4-.413-1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/></svg>
                        </button>
                    </div>
                    {showCollectionPopup && ( <div className="input-suggestion-popup collection-popup" style={{ position: 'fixed', bottom: popupPosition.bottom, left: popupPosition.left, minWidth: '200px', maxWidth: `calc(${popupPosition.width}px - 90px)` }} > {loadingCollections && <div className="popup-loading">Loading...</div>} {collectionError && <div className="popup-error">{collectionError}</div>} {!loadingCollections && !collectionError && filteredCollections.length === 0 && ( <div className="popup-no-results">No matching.</div> )} {!loadingCollections && !collectionError && filteredCollections.length > 0 && ( <ul className="popup-list"> {filteredCollections.map(name => ( <li key={name} className="popup-item" onMouseDown={e => { e.preventDefault(); handleCollectionSelect(name); }} > {name} </li> ))} </ul> )} </div> )}
                    {showDbPopup && ( <div className="input-suggestion-popup db-popup" style={{ position: 'fixed', bottom: popupPosition.bottom, left: popupPosition.left, minWidth: '200px', maxWidth: `calc(${popupPosition.width}px - 90px)` }} > {loadingDbs && <div className="popup-loading">Loading...</div>} {dbError && <div className="popup-error">{dbError}</div>} {!loadingDbs && !dbError && filteredDbs.length === 0 && ( <div className="popup-no-results">No matching.</div> )} {!loadingDbs && !dbError && filteredDbs.length > 0 && ( <ul className="popup-list"> {filteredDbs.map(name => ( <li key={name} className="popup-item" onMouseDown={e => { e.preventDefault(); handleDbSelect(name); }} > {name} </li> ))} </ul> )} </div> )}
                </div>
            </div>

            <ChatSettingsModal
                isOpen={isChatSettingsOpen}
                onSave={handleSaveSettingsAndPersist}     // MODIFIED Prop
                onCancel={handleCancelSettingsModal}    // MODIFIED Prop
                
                knowledgeNames={collectionNames}
                
                initialUseRAG={useRAG}
                initialSelectedKnowledgeName={selectedKnowledgeName}
                initialUseText2SQL={useText2SQL}
                initialSelectedDbForModal={selectedDb}
                initialUseAgentAPI={useAgentAPI}
                initialLoopsBeforeHumanInput={loopsBeforeHumanInput}
                initialSelectedTools={selectedTools}
                initialSelectedPipeline={selectedPipeline}
                initialSeeThoughtsAndActions={seeThoughtsAndActions}
                
                onSelectPrompt={(promptObj) => { 
                    setSystemPrompt(promptObj);
                }}
                initialAttachedFiles={attachedFiles}
                authUsername={authUsername}
            />
            <ExplorerLogModal
                isOpen={isExplorerModalOpen}
                onClose={() => {
                    setIsExplorerModalOpen(false);
                    setSelectedExplorerUUID(null);
                    setExplorerLogData(null);
                    setExplorerModalError('');
                }}
                logData={explorerLogData}
                isLoading={isExplorerModalLoading}
                error={explorerModalError}
            />
        </div>
    );
});

export default ChatWindow;